// src/pages/CareersPage.jsx
import React from 'react';
import { ArrowRight } from 'lucide-react';
import Button from '../components/ui/Button';
import JobListing from '../components/ui/JobListing';

const CareersPage = () => {
  // Sample job openings
  const jobOpenings = [
    {
      id: 'job1',
      title: 'Associate - Criminal Litigation',
      location: 'Delhi Office',
      experience: '2-5 years',
      description: 'We are looking for an Associate with experience in criminal litigation to join our growing team. The ideal candidate will have strong research skills and experience in court appearances.',
      qualifications: [
        'LLB from a recognized university',
        'Enrolled with the Bar Council',
        '2-5 years of experience in criminal litigation',
        'Excellent research and writing skills',
        'Proficiency in English and Hindi'
      ]
    },
    {
      id: 'job2',
      title: 'Legal Researcher',
      location: 'Remote / Delhi Office',
      experience: '1-3 years',
      description: 'Seeking a detail-oriented Legal Researcher to support our litigation team. This role involves comprehensive legal research, drafting legal memoranda, and assisting with case preparation.',
      qualifications: [
        'LLB/LLM degree from a recognized university',
        'Strong analytical and research skills',
        'Excellent writing ability',
        'Experience with legal databases and research tools',
        'Knowledge of various areas of law'
      ]
    },
    {
      id: 'job3',
      title: 'Senior Associate - Corporate Law',
      location: 'Noida Office',
      experience: '5-8 years',
      description: 'We are seeking an experienced Senior Associate for our Corporate Law department. The role involves handling complex corporate transactions, advising clients, and supervising junior associates.',
      qualifications: [
        'LLB from a top-tier law school',
        'Minimum 5 years of experience in corporate law',
        'Experience with mergers, acquisitions, and corporate restructuring',
        'Strong client management skills',
        'Team leadership capabilities'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="bg-primary text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Careers at AMV Law</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Join our team of dedicated legal professionals and build a rewarding career in law
          </p>
        </div>
      </div>

      {/* Why Join Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Why Join AMV Law</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Professional Growth</h3>
              <p className="text-gray-600 text-center">
                Work on complex, high-stakes matters alongside experienced mentors who are committed to your development
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Collaborative Culture</h3>
              <p className="text-gray-600 text-center">
                Be part of a supportive team that values your input and encourages collaboration across practice areas
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-lg shadow-md">
              <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Work-Life Balance</h3>
              <p className="text-gray-600 text-center">
                We recognize the importance of personal well-being and strive to maintain a healthy work-life balance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Current Openings Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Current Openings</h2>
          
          <div className="space-y-8 max-w-4xl mx-auto">
            {jobOpenings.map((job) => (
              <JobListing key={job.id} job={job} />
            ))}
          </div>
        </div>
      </section>

      {/* Application Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Application Process</h2>
          
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-0 md:left-1/2 transform md:-translate-x-1/2 h-full w-1 bg-primary-100"></div>
              
              {/* Timeline items */}
              <div className="space-y-12">
                <div className="relative">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className="flex-1 md:text-right md:pr-8 mb-4 md:mb-0">
                      <h3 className="text-xl font-bold text-gray-900">Submit Application</h3>
                      <p className="text-gray-600">Send your resume, cover letter, and relevant supporting documents</p>
                    </div>
                    <div className="absolute left-0 md:left-1/2 transform -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white">
                      1
                    </div>
                    <div className="flex-1 md:pl-8 md:text-left">
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className="flex-1 md:text-right md:pr-8 order-1 md:order-1 mb-4 md:mb-0">
                    </div>
                    <div className="absolute left-0 md:left-1/2 transform -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white">
                      2
                    </div>
                    <div className="flex-1 md:pl-8 order-2 md:order-2">
                      <h3 className="text-xl font-bold text-gray-900">Initial Screening</h3>
                      <p className="text-gray-600">Our HR team reviews your application and conducts an initial phone interview</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className="flex-1 md:text-right md:pr-8 mb-4 md:mb-0">
                      <h3 className="text-xl font-bold text-gray-900">Technical Evaluation</h3>
                      <p className="text-gray-600">Complete a legal writing assignment or case analysis to showcase your skills</p>
                    </div>
                    <div className="absolute left-0 md:left-1/2 transform -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white">
                      3
                    </div>
                    <div className="flex-1 md:pl-8">
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className="flex-1 md:text-right md:pr-8 order-1 md:order-1 mb-4 md:mb-0">
                    </div>
                    <div className="absolute left-0 md:left-1/2 transform -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white">
                      4
                    </div>
                    <div className="flex-1 md:pl-8 order-2 md:order-2">
                      <h3 className="text-xl font-bold text-gray-900">In-Person Interview</h3>
                      <p className="text-gray-600">Meet with our team for an in-depth discussion about your experience and fit</p>
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex flex-col md:flex-row items-center">
                    <div className="flex-1 md:text-right md:pr-8 mb-4 md:mb-0">
                      <h3 className="text-xl font-bold text-gray-900">Offer & Onboarding</h3>
                      <p className="text-gray-600">Selected candidates receive an offer letter and begin the onboarding process</p>
                    </div>
                    <div className="absolute left-0 md:left-1/2 transform -translate-y-1/2 md:-translate-x-1/2 flex items-center justify-center w-10 h-10 rounded-full bg-primary text-white">
                      5
                    </div>
                    <div className="flex-1 md:pl-8">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="bg-primary py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Don't See a Suitable Position?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            We're always looking for talented professionals to join our team. Send us your resume for future opportunities.
          </p>
          <Button 
            href="/contact" 
            variant="accent"
            size="lg"
            className="inline-flex items-center"
          >
            Contact Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;